import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "../context/ThemeContext";
import API_BASE_URL from "../config";

const regions = [
  { label: "Oradea", value: 0 },
  { label: "Arad", value: 2000 },
  { label: "Leș", value: 4000 },
  { label: "Leș 2", value: 8000 },
];

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [region, setRegion] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { darkMode } = useTheme();
  const [canAccessMarmurist, setCanAccessMarmurist] = useState(false);
  const [hasCommission, setHasCommission] = useState(false);
  const [commissionLimit, setCommissionLimit] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name);
      setRole(user.role);
      setPhone(user.phone || "");
      setCanAccessMarmurist(user.canAccessMarmurist || false);
      setRegion(user.region || 0);
      setHasCommission(user.hasCommission || false);
      setCommissionLimit(
        user.commissionLimit !== undefined && user.commissionLimit !== null
          ? user.commissionLimit
          : ""
      );
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");

      const payload = {
        name,
        phone,
        role,
        region,
        canAccessMarmurist,
        hasCommission,
        commissionLimit:
          commissionLimit === "" ? null : Number(commissionLimit),
      };

      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Eroare la actualizarea utilizatorului.");
      }

      setSuccessMessage("✅ Utilizator actualizat cu succes!");
      setTimeout(() => {
        setSuccessMessage("");
        onUserUpdated();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
    }
  };

  return (
    <div
      id="modal-overlay"
      className={`fixed inset-0 flex items-center justify-center ${darkMode ? "bg-black bg-opacity-80" : "bg-gray-300 bg-opacity-80"
        }`}
      onClick={handleOverlayClick}
    >
      <div
        className={`${darkMode ? "bg-gray-900 text-white" : "bg-white text-black"
          } p-6 rounded-2xl shadow-xl w-[400px] relative`}
        onClick={(e) => e.stopPropagation()} // Prevent closing on inner clicks
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: darkMode ? "red" : "darkred",
            "&:hover": { color: darkMode ? "darkred" : "red" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <h2
          className={`text-2xl font-semibold mb-4 text-center ${darkMode ? "text-blue-400" : "text-blue-600"
            }`}
        >
          Editare utilizator
        </h2>

        {successMessage && (
          <p className="text-green-500 bg-green-900 p-2 rounded mb-3 text-center">
            {successMessage}
          </p>
        )}

        {error && (
          <p className="text-red-500 bg-red-900 p-2 rounded mb-3 text-center">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Input */}
          <div>
            <label
              className={`block ${darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold mb-1`}
            >
              Nume
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
                }`}
              required
            />
          </div>

          {/* Role Dropdown */}
          <div>
            <label
              className={`block ${darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold mb-1`}
            >
              Rol
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
                }`}
            >
              <option value="Agent">Agent</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          {/* Region Dropdown */}
          <div>
            <label
              className={`block ${darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold mb-1`}
            >
              Regiune
            </label>
            <select
              value={region}
              onChange={(e) => setRegion(Number(e.target.value))}
              className={`w-full p-2 border rounded-2xl ${darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
                }`}
            >
              {regions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Phone Input */}
          <div>
            <label
              className={`block ${darkMode ? "text-gray-300" : "text-gray-700"
                } font-semibold mb-1`}
            >
              Nr. telefon
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full p-2 border rounded-2xl ${darkMode
                  ? "bg-gray-800 text-white border-gray-600"
                  : "bg-white text-black border-gray-400"
                }`}
              required
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={canAccessMarmurist}
                onChange={(e) => setCanAccessMarmurist(e.target.checked)}
                className="w-5 h-5"
              />
              <span
                className={`${darkMode ? "text-gray-300" : "text-gray-700"
                  } font-semibold`}
              >
                Acces la clienți marmiste
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={hasCommission}
                onChange={(e) => setHasCommission(e.target.checked)}
                className="w-5 h-5"
              />
              <span
                className={`font-semibold ${darkMode ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Eligibil pentru comision
              </span>
            </label>
          </div>

          {hasCommission && (
            <div>
              <label
                className={`block ${darkMode ? "text-gray-300" : "text-gray-700"
                  } font-semibold mb-1`}
              >
                Limită comision (€)
              </label>
              <input
                type="number"
                min={0}
                step={100}
                value={commissionLimit}
                onChange={(e) => setCommissionLimit(e.target.value)}
                className={`w-full p-2 border rounded-2xl ${darkMode
                    ? "bg-gray-800 text-white border-gray-600"
                    : "bg-white text-black border-gray-400"
                  }`}
                placeholder="Ex: 10000"
              />
            </div>
          )}

          <div className="h-1"></div>

          {/* Submit and Close Buttons */}
          <div className="mt-4 flex justify-between gap-4">
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: darkMode ? "#1976d2" : "#1565c0",
                borderRadius: "30px",
                fontSize: "1rem",
                color: "white",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { bgcolor: darkMode ? "#1565c0" : "#1976d2" },
              }}
              disabled={loading}
            >
              {loading ? "Se încarcă..." : "Salvează"}
            </Button>

            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                color: darkMode ? "#fff" : "#000",
                borderColor: darkMode ? "#fff" : "#000",
                borderRadius: "30px",
                fontSize: "1rem",
                padding: "10px",
                height: "56px",
                flex: 1,
                "&:hover": { borderColor: darkMode ? "#ccc" : "#bbb" },
              }}
            >
              Închide
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
